<!-- Toast Message: Disabled - status visible in UI -->
<!-- <p-toast position="top-center" key="global-toast"></p-toast> -->

<!-- Loading State -->
@if (isLoading()) {
  <div class="flex justify-content-center align-items-center" style="min-height: 400px;" role="status" aria-live="polite">
    <div class="text-center">
      <i class="pi pi-spinner pi-spin text-4xl text-primary" aria-hidden="true"></i>
      <p class="mt-3 text-color-secondary">Caricamento stato registrazione...</p>
    </div>
  </div>
}

<!-- Error State -->
@else if (hasError()) {
  <div class="flex justify-content-center align-items-center" style="min-height: 400px;" role="alert">
    <div class="text-center">
      <i class="pi pi-exclamation-triangle text-4xl text-red-500" aria-hidden="true"></i>
      <p class="mt-3 text-color-secondary">Errore nel caricamento dello stato</p>
      <p class="mt-2 text-sm text-color-secondary">Verifica di aver completato la pre-registrazione</p>
      <div class="flex gap-2 justify-content-center mt-3">
        <button pButton label="Riprova" icon="pi pi-refresh" (click)="refreshStep()" aria-label="Riprova a caricare lo stato di registrazione"></button>
        <button pButton label="Vai alla Pre-registrazione" icon="pi pi-arrow-right" severity="secondary" (click)="goToPreRegistration()" aria-label="Vai alla pagina di pre-registrazione"></button>
      </div>
    </div>
  </div>
}

<!-- Main Content -->
@else {
<div class="card-container flex justify-content-center">
  <p-card
    header="Progressione Registrazione"
    styleClass="w-full custom-card"
    [style]="{ 'max-width': '56rem' }"
  >
    <!-- Live region for dynamic updates -->
    <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {{ liveRegionMessage() }}
    </div>
    
    <!-- Modification Request Banner -->
    @if (hasModificationRequest()) {
      <div class="modification-request-banner mb-4 p-4 border-round-lg surface-0 border-2 border-orange-500 shadow-3" role="alert" aria-live="assertive">
        <div class="flex align-items-start gap-3">
          <div class="flex-shrink-0">
            <i class="pi pi-exclamation-triangle text-orange-500 text-3xl" aria-hidden="true"></i>
          </div>
          <div class="flex-1">
            <h3 class="text-orange-900 font-bold text-xl m-0 mb-2">
              <i class="pi pi-refresh mr-2" aria-hidden="true"></i>
              @if (isMfaModification()) {
                Richiesta di Modifica — Dati MFA
              } @else {
                Richiesta di Modifica — Anagrafica
              }
            </h3>
            @if (isMfaModification()) {
              <p class="text-orange-800 text-base m-0 mb-3">
                AXA ha richiesto delle modifiche ai dati MFA (codici SOL). Correggi i dati indicati e reinvia la compilazione.
              </p>
            } @else {
              <p class="text-orange-800 text-base m-0 mb-3">
                AXA ha richiesto delle modifiche ai dati anagrafici. È necessario correggere i dati e reinviare la domanda.
              </p>
            }
            @if (modificationComment()) {
              <div class="surface-50 border-round p-3 border-left-3 border-orange-500 mb-3">
                <p class="text-sm font-semibold text-600 m-0 mb-2">
                  <i class="pi pi-comment mr-2" aria-hidden="true"></i>
                  Note del Revisore:
                </p>
                <p class="text-base text-900 m-0 white-space-pre-wrap">{{ displayedComment() }}</p>
                @if (isCommentLong()) {
                  <button 
                    type="button"
                    class="text-sm text-orange-600 hover:text-orange-700 font-semibold cursor-pointer border-none bg-transparent p-0 mt-2"
                    (click)="toggleModificationNotes()"
                    [attr.aria-expanded]="isModificationNotesExpanded()"
                    aria-controls="modification-notes">
                    <i class="pi mr-1" [class.pi-chevron-down]="!isModificationNotesExpanded()" [class.pi-chevron-up]="isModificationNotesExpanded()" aria-hidden="true"></i>
                    {{ isModificationNotesExpanded() ? 'Mostra meno' : 'Visualizza altro' }}
                  </button>
                }
              </div>
            }
            @if (isMfaModification()) {
              <p-button
                label="Vai ai Dati MFA"
                icon="pi pi-arrow-right"
                iconPos="right"
                (onClick)="continueToStep(RegistrationStep.MFA_SETUP)"
                [outlined]="true"
                aria-label="Vai alla sezione dati MFA per effettuare le modifiche richieste"
              />
            } @else {
              <p-button
                label="Vai a Inserimento Dati"
                icon="pi pi-arrow-right"
                iconPos="right"
                (onClick)="continueToStep(RegistrationStep.DATA_INPUT)"
                [outlined]="true"
                aria-label="Vai alla sezione di inserimento dati per effettuare le modifiche richieste"
              />
            }
          </div>
        </div>
      </div>
    }
    
    <!-- Visualizzazione anagrafica (sola lettura): sempre disponibile dopo l'invio, anche su step successivi -->
    @if (canViewAnagrafica()) {
      <div class="mb-4 p-3 surface-50 border-round border-1 border-primary-200">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-eye text-2xl text-primary" aria-hidden="true"></i>
          <div class="flex-1">
            <h3 class="m-0 mb-1 text-lg font-semibold">Visualizza Anagrafica Inviata</h3>
            <p class="m-0 text-sm text-color-secondary">
              Puoi visualizzare i dati anagrafici inviati in modalità sola lettura
            </p>
          </div>
          <p-button
            label="Visualizza"
            icon="pi pi-arrow-right"
            iconPos="right"
            (onClick)="viewSubmittedData()"
            [outlined]="true"
            aria-label="Visualizza anagrafica inviata in modalità sola lettura"
          />
        </div>
      </div>
    }

    <!-- Visualizzazione dati MFA (sola lettura): disponibile dopo l'invio dei dati MFA -->
    @if (canViewMfaRecap()) {
      <div class="mb-4 p-3 surface-50 border-round border-1 border-primary-200">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-shield text-2xl text-primary" aria-hidden="true"></i>
          <div class="flex-1">
            <h3 class="m-0 mb-1 text-lg font-semibold">Visualizza Dati MFA Inviati</h3>
            <p class="m-0 text-sm text-color-secondary">
              Puoi visualizzare il riepilogo dei dati MFA (codici SOL) inviati in modalità sola lettura
            </p>
          </div>
          <p-button
            label="Visualizza"
            icon="pi pi-arrow-right"
            iconPos="right"
            (onClick)="viewMfaRecap()"
            [outlined]="true"
            aria-label="Visualizza dati MFA inviati in modalità sola lettura"
          />
        </div>
      </div>
    }

    <ol class="progress-list-container flex flex-column" role="list" aria-label="Passi della registrazione">
      @for (stepDisplay of steps(); track stepDisplay.step; let i = $index; let isLast = $last) {
        <li class="step-item flex align-items-start" 
            [class.active-step-row]="stepDisplay.isCurrent"
            [attr.aria-current]="stepDisplay.isCurrent ? 'step' : null">
          <!-- Icon Area with Vertical Line -->
          <div class="icon-wrapper">
            <div class="icon-container mr-3">
              <!-- Outer Circle -->
              <div
                class="outer-circle"
                [class.outer-completed]="stepDisplay.status === 'COMPLETED'"
                [class.outer-active]="stepDisplay.isCurrent && stepDisplay.status !== 'COMPLETED'"
                [class.outer-rejected]="stepDisplay.status === 'REJECTED'"
                [class.outer-modification-requested]="stepDisplay.status === 'MODIFICATION_REQUESTED'"
                [class.outer-locked]="!stepDisplay.isCurrent && stepDisplay.status !== 'COMPLETED' && stepDisplay.status !== 'REJECTED' && stepDisplay.status !== 'MODIFICATION_REQUESTED'"
              >
                <!-- Inner Circle with Icon -->
                <div
                  class="inner-circle"
                  [class.inner-completed]="stepDisplay.status === 'COMPLETED'"
                  [class.inner-active]="stepDisplay.isCurrent && stepDisplay.status !== 'COMPLETED'"
                  [class.inner-rejected]="stepDisplay.status === 'REJECTED'"
                  [class.inner-modification-requested]="stepDisplay.status === 'MODIFICATION_REQUESTED'"
                  [class.inner-locked]="!stepDisplay.isCurrent && stepDisplay.status !== 'COMPLETED' && stepDisplay.status !== 'REJECTED' && stepDisplay.status !== 'MODIFICATION_REQUESTED'"
                >
                  @if (stepDisplay.status === 'COMPLETED') {
                    <i class="pi pi-check" aria-hidden="true"></i>
                  } @else if (stepDisplay.status === 'REJECTED') {
                    <i class="pi pi-times" aria-hidden="true"></i>
                  } @else if (stepDisplay.status === 'MODIFICATION_REQUESTED') {
                    <i class="pi pi-refresh" aria-hidden="true"></i>
                  } @else if (stepDisplay.isCurrent && stepDisplay.status === 'USER_INPUT') {
                    <i class="pi pi-pencil" aria-hidden="true"></i>
                  } @else if (stepDisplay.status === 'PENDING' && stepDisplay.isCurrent) {
                    <!-- Step corrente in attesa (es. dati MFA inviati): non è bloccato, è "in corso". -->
                    <i class="pi pi-hourglass" aria-hidden="true"></i>
                  } @else if (stepDisplay.status === 'PENDING') {
                    <!-- Step futuro non ancora sbloccato dal flusso: lucchetto chiuso. -->
                    <i class="pi pi-lock" aria-hidden="true"></i>
                  } @else {
                    <i class="pi pi-clock" aria-hidden="true"></i>
                  }
                </div>
              </div>
            </div>
            <!-- Vertical Connecting Line -->
            @if (!isLast) {
              <div class="vertical-line"></div>
            }
          </div>

          <!-- Text and Button Area -->
          <div class="text-container flex flex-column flex-grow-1">
            <!-- Label Line with optional leading icon -->
            <div class="flex align-items-center">
              @if (stepDisplay.status === 'COMPLETED') {
                <i class="pi pi-check mr-2 status-completed-text" aria-hidden="true"></i>
              } @else if (stepDisplay.status === 'REJECTED') {
                <i class="pi pi-times-circle mr-2 status-rejected-text" aria-hidden="true"></i>
              } @else if (stepDisplay.status === 'MODIFICATION_REQUESTED') {
                <i class="pi pi-refresh mr-2 status-modification-requested-text" aria-hidden="true"></i>
              } @else if (stepDisplay.isCurrent && stepDisplay.status === 'USER_INPUT') {
                <i class="pi pi-exclamation-triangle mr-2 status-active-text" aria-hidden="true"></i>
              }
              <span class="step-label" role="heading" aria-level="3">{{ getStepLabel(stepDisplay.step, stepDisplay.status) }}</span>
            </div>

            <!-- Detail Line with subtitle and optional button -->
            <div class="step-detail mt-1 flex align-items-center gap-2">
              @if (stepDisplay.status === 'PENDING') {
                <i
                  class="pi mr-2 status-locked-text"
                  [class.pi-hourglass]="stepDisplay.isCurrent"
                  [class.pi-clock]="!stepDisplay.isCurrent"
                  aria-hidden="true"
                ></i>
              }
              <small class="detail-text">
                {{ getStepSubtitle(stepDisplay) }}
              </small>
              
              <!-- Action button for steps requiring user input -->
              @if (stepDisplay.isCurrent && (stepDisplay.status === 'USER_INPUT' || stepDisplay.status === 'REJECTED')) {
                <p-button
                  [label]="stepDisplay.status === 'REJECTED' ? 'Correggi' : 'Riprendi'"
                  size="small"
                  (onClick)="continueToStep(stepDisplay.step)"
                  styleClass="resume-button"
                  [severity]="stepDisplay.status === 'REJECTED' ? 'danger' : 'primary'"
                  [attr.aria-label]="getActionButtonLabel(stepDisplay)"
                />
              }
              <!-- Link to contract download when fully activated -->
              @if (stepDisplay.step === 'CONTRACT_COUNTERSIGNATURE' && stepDisplay.status === 'COMPLETED') {
                <p-button
                  label="Vai al contratto"
                  size="small"
                  (onClick)="goToContracts()"
                  styleClass="resume-button"
                  severity="primary"
                  aria-label="Vai alla pagina del contratto per scaricare il documento finale"
                />
              }
            </div>
          </div>
        </li>
      }
    </ol>
  </p-card>
</div>
}
